import { useForm } from "react-hook-form"
import { useEffect, useState } from "react"
import { useDebounce } from "@hooks/useDebounce"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { Textarea } from "@components/ui/textarea"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { formatDate } from "@utils/formatters"
import { useDrawer } from "@contexts/DrawerContext"
import { type IValueLabel } from "@apis/shared/types"
import type { IRejectAccountResponse } from "@apis/accounts/types"
import type { IECLResult } from "@apis/shoppers/types"
import { SearchAccountNumberForm } from "../SearchAccountNumber/SearchAccountNumberForm"
import { useRejectInformationSerializer } from "./utils/rejectInformationSerializer"
import {
  useSaveRejectMutation,
  useGetAccountExistsQuery,
} from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import type { IAccountExistsParams } from "@apis/accounts/types"
import { toast } from "sonner"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { Loader2 } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { AccountDuplicateStatusForm } from "../AccountStatusForms/AccountDuplicateStatusForm"
import { useHasPermissions } from "@hooks/useHasPermissions"

type TRejectInformationProps = {
  account_id: string
}

export const RejectInformation = ({ account_id }: TRejectInformationProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [dispositions, setDispositions] = useState<IValueLabel[] | undefined>()

  const getRejectInformationSerialized =
    useRejectInformationSerializer(account_id)
  const saveRejectMutation = useSaveRejectMutation()
  const { data: userInfo } = useAuthUserDetails()
  const queryClient = useQueryClient()
  const { openDrawer, closeDrawer } = useDrawer()
  const { canEditAccountStatus } = useHasPermissions()

  const form = useForm<IRejectAccountResponse>({
    mode: "onTouched",
    defaultValues: {
      account_number: "",
      reject_status_code: "",
      reject_disposition: "",
      notes: "",
    },
  })

  const {
    last_name,
    market_code,
    market,
    rejected_account_data,
    rejected_data,
    reject_statuses,
    default_dispositions,
    resubmit_dispositions,
  } = getRejectInformationSerialized.data
  const isPending = getRejectInformationSerialized.isPending

  const watchStatus = form.watch("reject_status_code")
  const watchAccountNumber = form.watch("account_number")
  const debouncedAccountNumber = useDebounce(watchAccountNumber, 300)

  // IIFE that builds params only when account number is valid
  const accountExistsParams: IAccountExistsParams | undefined = (() => {
    if (!debouncedAccountNumber || !market_code || !market) return undefined

    const accountNumberRegexp = new RegExp(
      market.validation_rules.account_number_regexp as string,
    )
    if (!accountNumberRegexp.test(debouncedAccountNumber)) return undefined

    return {
      account_number: debouncedAccountNumber,
      market_code,
    }
  })()

  // Query auto-fetches when params are defined (skipToken when undefined)
  const accountExistsQuery = useGetAccountExistsQuery(accountExistsParams)
  const accountExists = accountExistsQuery.data?.exists ?? false
  const isValidatingAccountNumber = accountExistsQuery.isFetching

  useEffect(() => {
    setDispositions(
      watchStatus === "RESUBMITTED"
        ? resubmit_dispositions
        : default_dispositions,
    )

    const currentDisposition = form.getValues("reject_disposition")
    const availableOptions =
      watchStatus === "RESUBMITTED"
        ? resubmit_dispositions
        : default_dispositions

    const isValidOption =
      !!currentDisposition &&
      availableOptions?.some((d) => d.value === currentDisposition)

    if (!isValidOption) {
      form.setValue("reject_disposition", "", { shouldValidate: true })
    }
  }, [watchStatus, form, resubmit_dispositions, default_dispositions])

  // Clear save message when account number changes
  useEffect(() => {
    setErrorMessage(null)
  }, [debouncedAccountNumber])

  useEffect(() => {
    if (rejected_data && rejected_account_data) {
      form.reset({
        account_number: rejected_data.account_number || "",
        reject_status_code: rejected_account_data.reject_status_code || "",
        reject_disposition: rejected_account_data.reject_disposition || "",
        notes: rejected_account_data.notes || "",
      })
    }
  }, [rejected_data, rejected_account_data, form])

  const handleSaveReject = (
    requestData: IRejectAccountResponse & { reject_rep: string },
  ) => {
    saveRejectMutation.mutate(
      { reject_id: requestData.reject_id, request: requestData },
      {
        onSuccess: () => {
          toast.success("Successfully saved reject information!")
          //Since we'll always want "fresh" data about account exisence, after saving a reject we'll invalidate Account Exists related queries
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountExists(accountExistsParams),
          })
        },
        onError: (error) =>
          toast.error(`Error saving reject information! ${error}`),
      },
    )
  }

  const handleSubmit = async (formData: IRejectAccountResponse) => {
    if (!formData.account_number || !market_code || !rejected_data || !userInfo)
      return

    const updatedReject = {
      ...rejected_data,
      ...formData,
      reject_rep: userInfo.email,
    }
    // IF reject_status_code === "WORKED" don't check for account existence and save reject
    if (formData.reject_status_code === "WORKED") {
      handleSaveReject(updatedReject)
      return
    }

    try {
      if (accountExists) {
        setErrorMessage("Account number already exists!")
        return
      }

      handleSaveReject(updatedReject)
    } catch (error) {
      setErrorMessage(`Error validating account. Error: ${error}`)
    }
  }

  const handleSelectCustomer = (customer: IECLResult) => {
    form.setValue("account_number", customer.account_number, {
      shouldValidate: true,
    })
    closeDrawer()
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:flex tw:flex-col tw:gap-6"
      >
        <p className="tw:text-lg tw:font-bold">Reject Information</p>

        <div className="tw:grid tw:grid-cols-2 tw:gap-6">
          <dl className="tw:space-y-1">
            <div>
              <dt className="tw:text-sm tw:font-medium">Reject Code</dt>
              <dd className="tw:text-sm">{rejected_data?.reject_code}</dd>
            </div>
            <div>
              <dt className="tw:text-sm tw:font-medium">Reject Reason</dt>
              <dd className="tw:text-sm">{rejected_data?.reject_reason}</dd>
            </div>
          </dl>

          <dl className="tw:space-y-1">
            <div>
              <dt className="tw:text-sm tw:font-medium">Secondary Id</dt>
              <dd className="tw:text-sm">
                {rejected_data?.secondary_account_id}
              </dd>
            </div>
            <div>
              <dt className="tw:text-sm tw:font-medium">Reject Date</dt>
              <dd className="tw:text-sm">
                {formatDate(rejected_data?.reject_dt, "MM/dd/yyyy")}
              </dd>
            </div>
          </dl>
        </div>

        <FormField
          control={form.control}
          name="account_number"
          rules={{
            required: `${market?.account_num_synonym} is required`,
            pattern: {
              value: new RegExp(
                market?.validation_rules.account_number_regexp as string,
              ),
              message: `Invalid ${market?.account_num_synonym} format. It should be ${market?.validation_rules.account_number_example}`,
            },
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="tw:capitalize">
                {market?.account_num_synonym}
              </FormLabel>
              <div className="tw:flex tw:gap-1">
                <FormControl>
                  <Input
                    placeholder={
                      market?.validation_rules.account_number_example
                    }
                    {...field}
                    className="tw:flex-1"
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() =>
                    openDrawer(
                      <SearchAccountNumberForm
                        market_code={market_code}
                        last_name={last_name}
                        onCancel={() => closeDrawer()}
                        onSelectCustomer={(customer) =>
                          handleSelectCustomer(customer)
                        }
                      />,
                      "Search for Account Number",
                    )
                  }
                >
                  Lookup Account Number
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="reject_status_code"
          rules={{ required: "Status is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl className="tw:w-1/2">
                  <SelectTrigger>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {reject_statuses?.map((options) => (
                    <SelectItem key={options.value} value={options.value}>
                      {options.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="reject_disposition"
          rules={{ required: "Disposition is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Disposition</FormLabel>
              <Select
                value={field.value ?? undefined}
                onValueChange={field.onChange}
              >
                <FormControl className="tw:w-1/2">
                  <SelectTrigger>
                    <SelectValue placeholder="Select Disposition" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {dispositions?.map((option) => (
                    <SelectItem key={option.label} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl className="tw:field-sizing-content">
                <Textarea
                  rows={4}
                  placeholder="Enter notes..."
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {errorMessage && (
          <p className="tw:text-md tw:font-semibold tw:text-red-500">
            {errorMessage}
          </p>
        )}
        <div className="tw:flex tw:justify-end tw:gap-1">
          <Button
            type="submit"
            disabled={
              saveRejectMutation.isPending ||
              isValidatingAccountNumber ||
              !form.formState.isValid
            }
          >
            {saveRejectMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Save</span>
            )}
          </Button>
          {canEditAccountStatus && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                openDrawer(
                  <AccountDuplicateStatusForm account_id={account_id} />,
                  "Update Account Status",
                )
              }}
            >
              Mark as Duplicate
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
